import axios from 'axios'
import { API_BASE_URL, API_TIMEOUT, TOKEN_KEY, REFRESH_TOKEN_KEY, ERROR_MESSAGES } from '@/utils/constants'

// Create axios instance
const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Queue for requests while refreshing token
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// Request interceptor - Attach auth token
axiosClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem(TOKEN_KEY)
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle errors and refresh token
axiosClient.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    const originalRequest = error.config

    // Network error
    if (!error.response) {
      return Promise.reject({
        message: ERROR_MESSAGES.NETWORK_ERROR,
        status: 0,
        originalError: error,
      })
    }

    const { status } = error.response

    // Handle 401 - Unauthorized (token expired)
    // Ignorer les erreurs 401 sur /api/auth/login, /api/auth/register, /api/auth/refresh
    // car ce sont des endpoints d'authentification qui peuvent légitimement retourner 401
    const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/login') || 
                          originalRequest?.url?.includes('/api/auth/register') ||
                          originalRequest?.url?.includes('/api/auth/refresh')
    
    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return axiosClient(originalRequest)
          })
          .catch(err => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

      if (!refreshToken) {
        // No refresh token, redirect to login silently (no toast)
        processQueue(new Error(ERROR_MESSAGES.UNAUTHORIZED), null)
        localStorage.clear()
        window.location.href = '/login'
        return Promise.reject({ message: ERROR_MESSAGES.UNAUTHORIZED })
      }

      try {
        // Call refresh token endpoint
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/refresh`,
          { refreshToken },
          { timeout: API_TIMEOUT }
        )

        // Backend returns StandardApiResponse<AuthResponse>, so data is in response.data.data
        const authData = response.data.data || response.data
        const { accessToken, refreshToken: newRefreshToken } = authData

        // Store new tokens
        localStorage.setItem(TOKEN_KEY, accessToken)
        if (newRefreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken)
        }

        // Update authorization header
        axiosClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`
        originalRequest.headers.Authorization = `Bearer ${accessToken}`

        // Process queued requests
        processQueue(null, accessToken)

        isRefreshing = false

        // Retry original request
        return axiosClient(originalRequest)
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError, null)
        isRefreshing = false

        localStorage.clear()
        window.location.href = '/login'

        return Promise.reject({
          message: ERROR_MESSAGES.UNAUTHORIZED,
          status: 401,
        })
      }
    }

    // Handle other error status codes
    const errorMessage = getErrorMessage(status, error.response.data)

    return Promise.reject({
      message: errorMessage,
      status,
      data: error.response.data,
      originalError: error,
    })
  }
)

/**
 * Get user-friendly error message based on status code
 */
const getErrorMessage = (status, data) => {
  // Check if backend provided a message
  if (data?.message) {
    return data.message
  }

  // Default messages based on status code
  switch (status) {
    case 400:
      return ERROR_MESSAGES.VALIDATION_ERROR
    case 401:
      return ERROR_MESSAGES.UNAUTHORIZED
    case 403:
      return ERROR_MESSAGES.FORBIDDEN
    case 404:
      return ERROR_MESSAGES.NOT_FOUND
    case 500:
    case 502:
    case 503:
    case 504:
      return ERROR_MESSAGES.SERVER_ERROR
    default:
      return ERROR_MESSAGES.UNKNOWN_ERROR
  }
}

/**
 * Helper function to make GET requests
 */
export const get = (url, config = {}) => {
  return axiosClient.get(url, config)
}

/**
 * Helper function to make POST requests
 */
export const post = (url, data = {}, config = {}) => {
  return axiosClient.post(url, data, config)
}

/**
 * Helper function to make PUT requests
 */
export const put = (url, data = {}, config = {}) => {
  return axiosClient.put(url, data, config)
}

/**
 * Helper function to make PATCH requests
 */
export const patch = (url, data = {}, config = {}) => {
  return axiosClient.patch(url, data, config)
}

/**
 * Helper function to make DELETE requests
 */
export const del = (url, config = {}) => {
  return axiosClient.delete(url, config)
}

/**
 * Upload file with progress tracking
 */
export const upload = (url, formData, onProgress) => {
  return axiosClient.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: progressEvent => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(percentCompleted)
      }
    },
  })
}

/**
 * Download file
 */
export const download = (url, filename, config = {}) => {
  return axiosClient.get(url, {
    ...config,
    responseType: 'blob',
  }).then(response => {
    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  })
}

export default axiosClient

